import { getCachedDateOfNow } from "#web/utils/getCachedDateOfNow";
import {
  BlockStack,
  Box,
  Button,
  DatePicker,
  Icon,
  InlineGrid,
  InlineStack,
  OptionList,
  Popover,
  Scrollable,
  TextField,
  useBreakpoints,
} from "@shopify/polaris";
import { ArrowRightIcon, CalendarIcon } from "@shopify/polaris-icons";
import { useCallback, useEffect, useState } from "react";

interface DateRangePickerProps {
  onDateRangeSelect: (dateRange: { end: Date; start: Date }) => void;
  value: { end: Date; start: Date };
}

export const DateRangePicker = ({ onDateRangeSelect, value: { end, start } }: DateRangePickerProps) => {
  const { mdDown } = useBreakpoints();
  const [popoverActive, setPopoverActive] = useState(false);

  const today = getCachedDateOfNow();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const ranges = [
    {
      period: {
        since: new Date(getCachedDateOfNow().setDate(today.getDate() - 7)),
        until: today,
      },
      title: "Last 7 days",
    },
    {
      period: {
        since: new Date(getCachedDateOfNow().setDate(today.getDate() - 30)),
        until: today,
      },
      title: "Last 30 days",
    },
    {
      period: {
        since: new Date(getCachedDateOfNow().setDate(today.getDate() - 90)),
        until: today,
      },
      title: "Last 90 days",
    },
    {
      period: {
        since: new Date(getCachedDateOfNow().setDate(today.getDate() - 365)),
        until: today,
      },
      title: "Last 365 days",
    },
  ];

  const getDefaultDateRange = () => {
    const areDatesEqual = (dateX: Date | null, dateY: Date | null) => {
      if (!dateX || !dateY) {
        return false;
      }
      return dateX.toDateString() === dateY.toDateString();
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (start && end) {
      // Adjust end date for comparison (subtract 1 day)
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() - 1);

      const currentRange = ranges.find((range) => {
        const { since, until } = range.period;
        return areDatesEqual(since, start) && areDatesEqual(until, adjustedEnd);
      });

      if (currentRange) {
        return currentRange;
      }
    }

    return ranges[2]!; // Default to "Last 90 days"
  };

  const defaultRange = getDefaultDateRange();
  const [activeDateRange, setActiveDateRange] = useState(defaultRange);
  const [dateState, setDateState] = useState({
    month: activeDateRange.period.since.getMonth(),
    year: activeDateRange.period.since.getFullYear(),
  });

  const handleMonthChange = useCallback((month: number, year: number) => {
    setDateState({ month, year });
  }, []);

  const toyyyyMMdd = (date: Date | null) => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  useEffect(() => {
    if (activeDateRange.period.until.getDate() === today.getDate()) {
      return;
    }

    // When component mounts, send the initial date range
    const endDate = new Date(activeDateRange.period.until);
    endDate.setDate(endDate.getDate() + 1); // Add one day to include the end date

    onDateRangeSelect({
      end: endDate,
      start: activeDateRange.period.since,
    });
  }, []);

  useEffect(() => {
    setDateState({
      month: activeDateRange.period.since.getMonth(),
      year: activeDateRange.period.since.getFullYear(),
    });
  }, [activeDateRange]);

  return (
    <Box>
      <Popover
        activator={
          <Button
            disclosure="down"
            icon={CalendarIcon}
            onClick={() => {
              setPopoverActive(!popoverActive);
            }}
            size="slim"
          >
            {activeDateRange.title}
          </Button>
        }
        active={popoverActive}
        autofocusTarget="none"
        fluidContent
        fullHeight
        onClose={() => {
          setPopoverActive(false);
        }}
        preferredAlignment="left"
        preferredPosition="below"
        sectioned={false}
      >
        <Box
          // @ts-expect-error style is not typed but I am not confident enough to remove it
          style={{
            paddingBottom: "16px",
            paddingLeft: "10px",
            paddingRight: "10px",
            paddingTop: "16px",
          }}
        >
          <Popover.Pane fixed>
            <InlineGrid columns={{ md: "max-content max-content", xs: "1fr" }} gap={"0"}>
              <Box
                maxWidth="212px"
                // @ts-expect-error style is not typed but I am not confident enough to remove it
                style={{
                  paddingRight: "20px",
                }}
                width="100%"
              >
                <Scrollable style={{ height: "auto" }}>
                  <OptionList
                    onChange={(selected) => {
                      const selectedRange = ranges.find((range) => range.title === selected[0]);
                      if (selectedRange) {
                        setActiveDateRange(selectedRange);
                      }
                    }}
                    options={ranges.map((range) => ({
                      label: (
                        <div
                          style={{
                            minWidth: "120px",
                          }}
                        >
                          {range.title}
                        </div>
                      ),
                      value: range.title,
                    }))}
                    selected={[activeDateRange.title]}
                  />
                </Scrollable>
              </Box>
              <Box maxWidth="516px">
                <BlockStack gap="400">
                  <InlineStack gap="200">
                    <div style={{ flexGrow: 1 }}>
                      <TextField
                        autoComplete="off"
                        label="Since"
                        readOnly
                        role="combobox"
                        value={toyyyyMMdd(activeDateRange.period.since)}
                      />
                    </div>
                    {!mdDown ? (
                      <Box
                        // @ts-expect-error style is not typed but I am not confident enough to remove it
                        style={{
                          marginTop: "5%",
                        }}
                      >
                        <Icon source={ArrowRightIcon} tone="subdued" />
                      </Box>
                    ) : null}

                    <div style={{ flexGrow: 1 }}>
                      <TextField
                        autoComplete="off"
                        label="Until"
                        readOnly
                        role="combobox"
                        value={toyyyyMMdd(activeDateRange.period.until)}
                      />
                    </div>
                  </InlineStack>
                  <div style={{ height: "256px" }}>
                    <DatePicker
                      allowRange
                      month={dateState.month}
                      multiMonth={mdDown ? false : true}
                      onChange={({ end, start }) => {
                        setActiveDateRange({
                          period: { since: start, until: end },
                          title: "Custom",
                        });
                      }}
                      onMonthChange={handleMonthChange}
                      selected={{
                        end: activeDateRange.period.until,
                        start: activeDateRange.period.since,
                      }}
                      year={dateState.year}
                    />
                  </div>
                </BlockStack>
              </Box>
            </InlineGrid>
          </Popover.Pane>
          <Popover.Pane fixed>
            <Popover.Section>
              <InlineStack align="end" gap="200">
                <Button
                  onClick={() => {
                    setActiveDateRange(defaultRange);
                    setPopoverActive(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    // Add one day to the until date for API queries (to include the end date)
                    const endDate = new Date(activeDateRange.period.until);
                    endDate.setDate(endDate.getDate() + 1);

                    onDateRangeSelect({
                      end: endDate,
                      start: activeDateRange.period.since,
                    });

                    setPopoverActive(false);
                  }}
                  variant="primary"
                >
                  Apply
                </Button>
              </InlineStack>
            </Popover.Section>
          </Popover.Pane>
        </Box>
      </Popover>
    </Box>
  );
};
